import { memo, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { PieLabelProps } from 'recharts/types/polar/Pie';

import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { useFormFactor } from '../../../utils/formFactor';
import { Breakpoint } from '../../../utils/breakpoint';

import { IWheelProps } from './types';

/**
 * Wrapper component that selects an optimized layout by form factor:
 * - desktop
 * - mobile-portrait
 * - mobile-landscape
 * - side list labels (psychologist tips mode)
 */
export const WheelOfBalance = (
  props: IWheelProps & {
    fullscreen?: boolean;
    disableHover?: boolean;
    expanded?: boolean;
    clientMobileLayout?: boolean;
    hideOuterLabels?: boolean;
    /** Labels as left/right lists; no titles on the wheel (tips mode). */
    sideListLabels?: boolean;
  },
) => {
  if (props.sideListLabels) {
    return <SideLabelsWheel {...props} disableHover={props.disableHover} />;
  }

  const formFactor = useFormFactor();
  const useClientMobileLayout =
    props.clientMobileLayout && (formFactor === 'mobile-portrait' || formFactor === 'mobile-landscape');
  const usePhoneListLayout =
    !props.hideOuterLabels &&
    (formFactor === 'mobile-portrait' || formFactor === 'mobile-landscape' || useClientMobileLayout);

  if (usePhoneListLayout) {
    if (formFactor === 'mobile-landscape') {
      return <MobileLandscapeWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
    }
    return <MobilePortraitWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
  }

  return <DesktopWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
};

// =========================
// Shared utilities & hooks
// =========================
const colors = {
  rings: '#d3d3d3',
  spokes: '#808080',
  label: '#111',
};

/** Same face as `` in the panel — needed for SVG / fullscreen portal. */
const WHEEL_FONT_FAMILY = 'Nunito, sans-serif';

const RAD = Math.PI / 180;
const RINGS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

// Multi-line outer labels are used for all sector counts (1–15).
const DENSE_LABEL_MAX_LINES = 3;
// Base gap from the wheel edge; top/bottom labels get extra clearance equal to
// half the text-block height so multi-line labels never sit on the rim.
const DENSE_LABEL_GAP = 28;
const DENSE_LABEL_DENSE_GAP = 12;
const DENSE_LABEL_FONT_WEIGHT = 700;
const DENSE_LABEL_ARC_SHARE = 0.85;
const DENSE_LABEL_AVERAGE_CHAR_WIDTH = 0.55;
const DENSE_LABEL_MIN_LINE_LENGTH = 10;
const DENSE_LABEL_LAYOUTS = [
  { maxSectors: 8, fontSize: 15, maxLineLength: 18 },
  { maxSectors: 12, fontSize: 12, maxLineLength: 16 },
  { maxSectors: 15, fontSize: 11, maxLineLength: 14 },
] as const;
const DENSE_LABEL_NARROW_LAYOUTS = [
  { maxSectors: 8, fontSize: 12, maxLineLength: 14 },
  { maxSectors: 12, fontSize: 10, maxLineLength: 12 },
  { maxSectors: 15, fontSize: 9, maxLineLength: 10 },
] as const;
// LabelRing's own Pie is drawn with outerRadius="110%" (see LabelRing below),
// so the `outerRadius` recharts hands to the label renderer is 110% of the
// chart's true radius, not the 100% edge where the colored wheel ends.
const LABEL_RING_OUTER_RADIUS_PERCENT = 110;
const WHEEL_EDGE_RATIO = 100 / LABEL_RING_OUTER_RADIUS_PERCENT;

function buildSectors(count: number) {
  return Array.from({ length: count }, (_, i) => ({ name: String(i), value: 10 }));
}

function getContrastColorForHex(hex: string) {
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma > 186 ? '#000' : '#fff';
}

function wrapAndTruncateLabel(
  text: string,
  maxLineLength: number,
  maxLines: number,
): { lines: string[]; truncated: boolean } {
  let remaining = text.trim().replace(/\s+/g, ' ');
  const lines: string[] = [];

  while (remaining && lines.length < maxLines) {
    if (remaining.length <= maxLineLength) {
      lines.push(remaining);
      remaining = '';
      break;
    }

    const wordBoundary = remaining.lastIndexOf(' ', maxLineLength);
    const splitAt = wordBoundary > 0 ? wordBoundary : maxLineLength;
    lines.push(remaining.slice(0, splitAt).trimEnd());
    remaining = remaining.slice(splitAt).trimStart();
  }

  const truncated = remaining.length > 0;
  if (truncated && lines.length > 0) {
    const lastLine = lines[lines.length - 1];
    lines[lines.length - 1] =
      lastLine.length < maxLineLength
        ? `${lastLine}…`
        : `${lastLine.slice(0, Math.max(1, maxLineLength - 1)).trimEnd()}…`;
  }

  return { lines, truncated };
}

const LABEL_TOOLTIP_WIDTH = 200;
const LABEL_TOOLTIP_GAP = 8;

function CompactDenseLabel({
  text,
  lines,
  truncated,
  textX,
  startY,
  fontSize,
  hitWidth,
  hitHeight,
  tooltipPlacement,
}: {
  text: string;
  lines: string[];
  truncated: boolean;
  textX: number;
  startY: number;
  fontSize: number;
  hitWidth: number;
  hitHeight: number;
  tooltipPlacement: 'top' | 'bottom';
}) {
  const [hover, setHover] = useState(false);
  const hitRef = useRef<SVGRectElement | null>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placeAbove: false });
  const lineHeight = fontSize * 1.2;
  const foX = textX - hitWidth / 2;
  const foY = startY - lineHeight / 2;
  const foW = Math.max(1, hitWidth);
  const foH = Math.max(1, hitHeight);

  const handleEnter = () => {
    if (!truncated || !hitRef.current) return;
    const rect = hitRef.current.getBoundingClientRect();
    const placeAbove = tooltipPlacement === 'top';
    let left = rect.right - LABEL_TOOLTIP_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - LABEL_TOOLTIP_WIDTH - 8));
    setCoords({
      top: placeAbove ? rect.top - LABEL_TOOLTIP_GAP : rect.bottom + LABEL_TOOLTIP_GAP,
      left,
      placeAbove,
    });
    setHover(true);
  };

  return (
    <g aria-label={text} style={{ cursor: truncated ? 'pointer' : 'default' }}>
      <text
        x={textX}
        y={startY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#1e2939"
        fontSize={fontSize}
        fontWeight={DENSE_LABEL_FONT_WEIGHT}
        fontFamily={WHEEL_FONT_FAMILY}
        style={{ cursor: truncated ? 'pointer' : 'default' }}
        pointerEvents="none"
      >
        {lines.map((line, lineIdx) => (
          <tspan key={`dense-label-line-${lineIdx}`} x={textX} dy={lineIdx === 0 ? 0 : '1.2em'}>
            {line}
          </tspan>
        ))}
      </text>
      {truncated ? (
        <rect
          ref={hitRef}
          x={foX}
          y={foY}
          width={foW}
          height={foH}
          fill="transparent"
          pointerEvents="all"
          onMouseEnter={handleEnter}
          onMouseLeave={() => setHover(false)}
        />
      ) : null}
      {hover &&
        truncated &&
        createPortal(
          <div
            className="fixed z-popover w-[200px] bg-white p-[6px_10px] rounded-chip text-ui text-ink shadow-popup border border-primary/50"
            style={{
              top: coords.top,
              left: coords.left,
              transform: coords.placeAbove ? 'translateY(-100%)' : undefined,
              pointerEvents: 'none',
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </g>
  );
}

function isCompactWheel(_sectorCount: number) {
  // Always use the multi-line outer-label layout (+ in-sector score labels).
  return true;
}

// =========================
// Shared chart primitives
// =========================
function useWheelState(values: number[], sectorCount: number, saveResult?: (v: number[]) => void) {
  const sectors = useMemo(() => buildSectors(sectorCount), [sectorCount]);
  const rr = RINGS;

  const [hoveredSector, setHoveredSector] = useState<number | null>(null);
  const [hoveredLevel, setHoveredLevel] = useState<number | null>(null);

  const hoverOuter = useMemo(() => (hoveredLevel ? `${hoveredLevel * 10}%` : null), [hoveredLevel]);

  const changeValue = useCallback(
    (index: number, value: number) => {
      if (!saveResult || values[index] === value) return;
      const next = [...values];
      next[index] = value;
      saveResult(next);
    },
    [saveResult, values],
  );

  return {
    sectors,
    rr,
    hoveredSector,
    setHoveredSector,
    hoveredLevel,
    setHoveredLevel,
    hoverOuter,
    changeValue,
  } as const;
}

const Rings = memo(function Rings({ rr, sectorCount }: { rr: number[]; sectorCount: number }) {
  return (
    <>
      {rr.map((r, i) => (
        <Pie
          key={`ring-${i}`}
          data={[{ name: 'ring', value: 8 }]}
          isAnimationActive={false}
          startAngle={90}
          endAngle={-270}
          innerRadius={i === 0 ? '0%' : `${r - 10}%`}
          outerRadius={`${r}%`}
          paddingAngle={0}
          dataKey="value"
          fill="transparent"
          stroke={colors.rings}
          strokeWidth={1}
        />
      ))}
      {Array.from({ length: sectorCount }, (_, i) => (
        <Pie
          key={`spoke-${i}`}
          data={[{ name: 'sp', value: 1 }]}
          isAnimationActive={false}
          startAngle={90 - i * (360 / sectorCount)}
          endAngle={90 - (i + 1) * (360 / sectorCount)}
          innerRadius={'0%'}
          outerRadius={'100%'}
          paddingAngle={0}
          dataKey="value"
          fill="transparent"
          stroke={colors.spokes}
          strokeWidth={1}
        />
      ))}
    </>
  );
});

const HoverPreview = memo(function HoverPreview({
  hoveredSector,
  hoverOuter,
  lifeItems,
}: {
  hoveredSector: number | null;
  hoverOuter: string | null;
  lifeItems: IWheelProps['lifeItems'];
}) {
  const sectorAngle = 360 / lifeItems.length;
  return hoveredSector !== null && hoverOuter ? (
    <Pie
      data={[{ name: 'hover', value: 1 }]}
      isAnimationActive={false}
      animationDuration={0}
      animationEasing="linear"
      startAngle={90 - hoveredSector * sectorAngle}
      endAngle={90 - (hoveredSector + 1) * sectorAngle}
      innerRadius={'0%'}
      outerRadius={hoverOuter}
      paddingAngle={0}
      dataKey="value"
      stroke={lifeItems[hoveredSector].color}
      strokeWidth={2}
      fill={lifeItems[hoveredSector].color}
      fillOpacity={0.25}
    />
  ) : null;
});

function InteractionRings({
  sectors,
  onEnter,
  onLeave,
  onClick,
}: {
  sectors: { name: string; value: number }[];
  onEnter: (idx: number, level: number) => void;
  onLeave: () => void;
  onClick?: (idx: number, level: number) => void;
}) {
  const activePointerIdRef = useRef<number | null>(null);

  const getLevelFromEvent = (e: any): number | null => {
    const target = e?.currentTarget as SVGElement | undefined;
    const svg = target?.ownerSVGElement;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e?.clientX ?? 0) - cx;
    const dy = (e?.clientY ?? 0) - cy;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxRadius =
      Array.from(svg.querySelectorAll('path')).reduce((radius, path) => {
        const bounds = path.getBoundingClientRect();
        const isCenteredCircle =
          Math.abs(bounds.width - bounds.height) <= 2 &&
          Math.abs(bounds.left + bounds.width / 2 - cx) <= 2 &&
          Math.abs(bounds.top + bounds.height / 2 - cy) <= 2;

        return isCenteredCircle ? Math.max(radius, bounds.width / 2) : radius;
      }, 0) || Math.min(rect.width, rect.height) / 2;

    if (maxRadius <= 0 || distance > maxRadius * 1.02) return null;

    const level = Math.ceil((distance / maxRadius) * 10);
    return Math.max(1, Math.min(10, level));
  };

  const applySelection = (e: any, idx: number) => {
    const level = getLevelFromEvent(e);
    if (level === null) return;
    onEnter(idx, level);
    if (onClick) onClick(idx, level);
  };

  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={'0%'}
      outerRadius={'100%'}
      paddingAngle={0}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
    >
      {sectors.map((_, idx) => (
        <Cell
          key={`int-cell-${idx}`}
          fill="#000"
          fillOpacity={0.001}
          style={{ pointerEvents: 'all' }}
          onPointerDown={(e: any) => {
            activePointerIdRef.current = e?.pointerId ?? null;
            e?.preventDefault?.();
            (e?.currentTarget as any)?.setPointerCapture?.(e?.pointerId);
            applySelection(e, idx);
          }}
          onPointerMove={(e: any) => {
            if (activePointerIdRef.current === null) return;
            if (e?.pointerId !== activePointerIdRef.current) return;
            applySelection(e, idx);
          }}
          onPointerUp={(e: any) => {
            if (activePointerIdRef.current === e?.pointerId) {
              activePointerIdRef.current = null;
              onLeave();
            }
          }}
          onPointerCancel={(e: any) => {
            if (activePointerIdRef.current === e?.pointerId) {
              activePointerIdRef.current = null;
              onLeave();
            }
          }}
          onMouseLeave={() => {
            if (activePointerIdRef.current === null) {
              onLeave();
            }
          }}
          cursor="pointer"
        />
      ))}
    </Pie>
  );
}

const FilledSectors = memo(function FilledSectors({
  sectors,
  values,
  lifeItems,
}: Pick<IWheelProps, 'values' | 'lifeItems'> & { sectors: { name: string; value: number }[] }) {
  return (
    <>
      {sectors.map((_, idx) => {
        const sectorAngle = 360 / sectors.length;
        const start = 90 - idx * sectorAngle;
        const end = start - sectorAngle;
        const outer = `${values[idx] * 10}%`;
        return (
          <Pie
            key={`fill-${idx}`}
            data={[{ name: `s-${idx}`, value: 1 }]}
            isAnimationActive={false}
            startAngle={start}
            endAngle={end}
            innerRadius={'0%'}
            outerRadius={outer}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            fill={lifeItems[idx].color}
            fillOpacity={0.7}
          />
        );
      })}
    </>
  );
});

const ScoreLabels = memo(function ScoreLabels({
  sectors,
  values,
}: {
  sectors: { name: string; value: number }[];
  values: IWheelProps['values'];
}) {
  const small = sectors.length > 12;
  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={'0%'}
      outerRadius={'100%'}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
      label={(props: PieLabelProps) => {
        const idx = sectors.findIndex((s) => s.name === (props.name as string));
        if (idx < 0 || !values[idx]) return null;
        const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0 } = props as Required<PieLabelProps>;
        const scoreRadius = Math.max(outerRadius * (values[idx] / 10) * 0.55, outerRadius * 0.12);
        const x = cx + scoreRadius * Math.cos(-midAngle * RAD);
        const y = cy + scoreRadius * Math.sin(-midAngle * RAD);
        return (
          <text
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#fff"
            fontSize={small ? 8 : 10}
            fontWeight={700}
            fontFamily={WHEEL_FONT_FAMILY}
            style={{ pointerEvents: 'none' }}
          >
            {values[idx]}
          </text>
        );
      }}
    >
      {sectors.map((_, i) => (
        <Cell key={`score-${i}`} fill="transparent" />
      ))}
    </Pie>
  );
});

const MOBILE_BADGE_GAP = 4;

const MobileSectorBadges = memo(function MobileSectorBadges({
  sectors,
  lifeItems,
}: Pick<IWheelProps, 'lifeItems'> & { sectors: { name: string; value: number }[] }) {
  const small = sectors.length > 12;
  const badgeR = small ? 8 : 10;
  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={'102%'}
      outerRadius={'110%'}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
      label={(props: PieLabelProps) => {
        const idx = sectors.findIndex((s) => s.name === (props.name as string));
        if (idx < 0) return null;
        const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0 } = props as Required<PieLabelProps>;
        const wheelEdge = outerRadius * WHEEL_EDGE_RATIO;
        const r = wheelEdge + MOBILE_BADGE_GAP + badgeR;
        const rad = -midAngle * RAD;
        const x = cx + r * Math.cos(rad);
        const y = cy + r * Math.sin(rad);
        const bg = lifeItems[idx].color;
        const fg = getContrastColorForHex(bg);
        return (
          <g style={{ pointerEvents: 'none' }}>
            <circle cx={x} cy={y} r={badgeR} fill={bg} stroke="rgba(0,0,0,0.1)" strokeWidth={1} />
            <text
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={fg}
              fontSize={small ? 9 : 11}
              fontWeight={700}
              fontFamily={WHEEL_FONT_FAMILY}
            >
              {idx + 1}
            </text>
          </g>
        );
      }}
    >
      {sectors.map((_, i) => (
        <Cell key={`badge-${i}`} fill="transparent" style={{ pointerEvents: 'none' }} />
      ))}
    </Pie>
  );
});

function makeOuterLabelRenderer({
  lifeItems,
  values,
  preferShortTitle: _preferShortTitle,
  avgChar,
  maxTextW,
  compact,
  labelGap = DENSE_LABEL_GAP,
  layouts = DENSE_LABEL_LAYOUTS,
}: {
  lifeItems: IWheelProps['lifeItems'];
  values: IWheelProps['values'];
  preferShortTitle?: boolean;
  avgChar: number;
  maxTextW: number;
  compact?: boolean;
  labelGap?: number;
  layouts?: readonly { maxSectors: number; fontSize: number; maxLineLength: number }[];
}) {
  return (idx: number) => (props: PieLabelProps) => {
    const item = lifeItems[idx];
    if (!item) return null;

    const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0 } = props as Required<PieLabelProps>;
    const text = item.title;

    if (compact) {
      const layout = layouts.find(({ maxSectors }) => lifeItems.length <= maxSectors) ?? layouts[layouts.length - 1];
      const rad = -midAngle * RAD;
      const wheelEdge = outerRadius * WHEEL_EDGE_RATIO;
      const sectorAngle = 360 / lifeItems.length;
      // Provisional radius for arc-width estimate; final radius includes text clearance.
      const provisionalLabelRadius = wheelEdge + labelGap;
      const availableArcLength = provisionalLabelRadius * sectorAngle * RAD * DENSE_LABEL_ARC_SHARE;
      const maxLineLength = Math.max(
        DENSE_LABEL_MIN_LINE_LENGTH,
        Math.min(
          layout.maxLineLength,
          Math.floor(availableArcLength / (layout.fontSize * DENSE_LABEL_AVERAGE_CHAR_WIDTH)),
        ),
      );
      const lineHeight = layout.fontSize * 1.2;
      const { lines, truncated } = wrapAndTruncateLabel(text, maxLineLength, DENSE_LABEL_MAX_LINES);
      const totalH = lines.length * lineHeight;
      const approxCharW = layout.fontSize * DENSE_LABEL_AVERAGE_CHAR_WIDTH;
      const foW = Math.ceil(Math.max(...lines.map((line) => line.length), 1) * approxCharW) + 8;
      // Multi-line labels are centered on the anchor. Half of the text block
      // grows toward the wheel — push the anchor out by that inward extent.
      const radialClearance = (totalH / 2) * Math.abs(Math.sin(rad)) + (foW / 2) * Math.abs(Math.cos(rad));
      const labelRadius = wheelEdge + labelGap + radialClearance;
      const lx = cx + labelRadius * Math.cos(rad);
      const ly = cy + labelRadius * Math.sin(rad);
      const startY = ly - totalH / 2 + lineHeight / 2;
      // Bottom half of the wheel (sin > 0 in SVG coords) — show tooltip above the label.
      const tooltipPlacement = Math.sin(rad) > 0.15 ? 'top' : 'bottom';

      return (
        <CompactDenseLabel
          text={text}
          lines={lines}
          truncated={truncated}
          textX={lx}
          startY={startY}
          fontSize={layout.fontSize}
          hitWidth={foW}
          hitHeight={totalH}
          tooltipPlacement={tooltipPlacement}
        />
      );
    }

    const edgeGap = 0;
    const badgeD = 34;
    const badgeR = badgeD / 2;

    const r = outerRadius + edgeGap + badgeR;
    const anchorX = Math.round(cx + r * Math.cos(midAngle * RAD));
    const anchorY = Math.round(cy - r * Math.sin(midAngle * RAD));

    const innerGap = 6;
    const rawTextW = Math.ceil(text.length * avgChar);
    const textW = Math.min(rawTextW, maxTextW);

    const boxW = badgeD + innerGap + textW;
    const boxH = 44;
    const alignRightSide = anchorX > cx;

    const boxX = alignRightSide ? anchorX - badgeR : anchorX - (textW + innerGap + badgeR);
    const boxY = anchorY - boxH / 2;

    const fg = getContrastColorForHex(item.color);

    return (
      <foreignObject
        x={boxX}
        y={boxY}
        width={boxW}
        height={boxH}
        style={{ overflow: 'visible', pointerEvents: 'none' }}
      >
        <div
          style={{
            fontFamily: WHEEL_FONT_FAMILY,
            fontSize: '16px',
            color: colors.label,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '6px',
            flexDirection: 'row',
            textAlign: 'left',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              minWidth: '34px',
              minHeight: '34px',
              borderRadius: '50%',
              backgroundColor: item.color,
              color: fg,
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '34px',
              textAlign: 'center',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
              pointerEvents: 'none',
            }}
          >
            {values[idx] === 0 ? '' : values[idx]}
          </span>
          <span style={{ pointerEvents: 'none' }}>{text}</span>
        </div>
      </foreignObject>
    );
  };
}

const LabelRing = memo(function LabelRing({
  sectors,
  lifeItems,
  values,
  preferShortTitle = false,
  avgChar = 25,
  maxTextW = 240,
}: {
  sectors: { name: string; value: number }[];
  lifeItems: IWheelProps['lifeItems'];
  values: IWheelProps['values'];
  preferShortTitle?: boolean;
  avgChar?: number;
  maxTextW?: number;
}) {
  const compact = isCompactWheel(sectors.length);
  const breakpoint = useBreakpoint();
  const isNarrow = breakpoint < Breakpoint.Xl;
  const labelGap = lifeItems.length >= 9 || isNarrow ? DENSE_LABEL_DENSE_GAP : DENSE_LABEL_GAP;
  const layouts = isNarrow ? DENSE_LABEL_NARROW_LAYOUTS : DENSE_LABEL_LAYOUTS;
  const renderOuterLabel = makeOuterLabelRenderer({
    lifeItems,
    values,
    preferShortTitle,
    avgChar,
    maxTextW,
    compact,
    labelGap,
    layouts,
  });
  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={compact ? '106%' : '102%'}
      outerRadius={compact ? '110%' : '110%'}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
      label={(p: PieLabelProps) => {
        const idx = sectors.findIndex((s) => s.name === (p.name as string));
        if (idx < 0) return null;
        return renderOuterLabel(idx)(p);
      }}
    >
      {sectors.map((_, i) => (
        <Cell key={`lab-${i}`} fill="transparent" style={{ pointerEvents: 'none' }} />
      ))}
    </Pie>
  );
});

function MobileTitle({
  idx,
  lifeItems,
  side: _side = 'left',
}: {
  idx: number;
  lifeItems: IWheelProps['lifeItems'];
  side?: 'left' | 'right';
  /** @deprecated short titles are no longer used */
  useShortTitle?: boolean;
}) {
  const text = lifeItems[idx].title;
  const bg = lifeItems[idx].color;
  const fg = getContrastColorForHex(bg);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [popup, setPopup] = useState<{ top: number; left: number } | null>(null);

  const closePopup = useCallback(() => setPopup(null), []);

  useEffect(() => {
    if (!popup) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rowRef.current?.contains(event.target as Node)) return;
      closePopup();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePopup();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [popup, closePopup]);

  const openPopup = () => {
    if (!rowRef.current) return;
    if (popup) {
      closePopup();
      return;
    }
    const rect = rowRef.current.getBoundingClientRect();
    const width = Math.min(260, window.innerWidth - 16);
    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setPopup({ top: rect.bottom + LABEL_TOOLTIP_GAP, left });
  };

  return (
    <>
      <div
        ref={rowRef}
        role="button"
        tabIndex={0}
        title={text}
        aria-label={text}
        onClick={openPopup}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPopup();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexDirection: 'row',
          textAlign: 'left',
          minWidth: 0,
          width: '100%',
          boxSizing: 'border-box',
          cursor: 'pointer',
          fontFamily: WHEEL_FONT_FAMILY,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            minWidth: '20px',
            minHeight: '20px',
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: bg,
            color: fg,
            fontSize: '12px',
            fontWeight: 700,
            lineHeight: '20px',
            textAlign: 'center',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
          }}
        >
          {idx + 1}
        </span>
        <span
          style={{
            fontSize: '14px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {text}
        </span>
      </div>
      {popup &&
        createPortal(
          <div
            className="fixed z-popover max-w-[min(260px,calc(100vw-16px))] bg-white p-[8px_12px] rounded-control text-ui text-ink shadow-popup border border-primary/50"
            style={{
              top: popup.top,
              left: popup.left,
              pointerEvents: 'none',
            }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}

// =========================
// Desktop
// =========================
function DesktopWheel({
  lifeItems,
  values,
  saveResult,
  fullscreen,
  disableHover,
  expanded,
  clientMobileLayout,
  hideOuterLabels,
}: IWheelProps & {
  fullscreen?: boolean;
  disableHover?: boolean;
  expanded?: boolean;
  clientMobileLayout?: boolean;
  hideOuterLabels?: boolean;
}) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, lifeItems.length, saveResult);
  const compact = isCompactWheel(lifeItems.length);
  const breakpoint = useBreakpoint();
  // 9+ sectors use denser outer labels — less margin, slightly larger wheel.
  const chartMargin =
    lifeItems.length >= 9 ? (breakpoint < Breakpoint.Xl ? 44 : 56) : breakpoint < Breakpoint.Xl ? 56 : 72;

  const chart = (
    <PieChart
      className="recharts__pie-chart cursor-pointer"
      margin={{ top: chartMargin, right: chartMargin, bottom: chartMargin, left: chartMargin }}
    >
      {!disableHover && <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />}
      <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
      <Rings rr={rr} sectorCount={lifeItems.length} />
      {compact && <ScoreLabels sectors={sectors} values={values} />}
      <InteractionRings
        sectors={sectors}
        onEnter={
          disableHover
            ? () => {}
            : (idx, lvl) => {
                setHoveredSector(idx);
                setHoveredLevel(lvl);
              }
        }
        onLeave={
          disableHover
            ? () => {}
            : () => {
                setHoveredSector(null);
                setHoveredLevel(null);
              }
        }
        onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
      />
      {!hideOuterLabels && <LabelRing sectors={sectors} lifeItems={lifeItems} values={values} />}
    </PieChart>
  );

  return (
    <div className="w-full h-full cursor-pointer">
      {fullscreen ? (
        <div className="flex h-full w-full items-center justify-center">
          <ResponsiveContainer width="88%" height="88%" style={{ margin: '0 auto' }}>
            {chart}
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center cursor-pointer">
          <ResponsiveContainer
            width={clientMobileLayout || expanded ? '100%' : '56%'}
            height={expanded ? '100%' : '92%'}
            style={{
              padding: clientMobileLayout ? '1rem' : expanded ? '0.25rem' : '3rem',
              margin: '0 auto',
              overflow: hideOuterLabels ? 'hidden' : 'visible',
              cursor: 'pointer',
            }}
          >
            {chart}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// =========================
// Side list labels (tips mode): left | wheel | right
// =========================
function SideLabelsWheel({ lifeItems, values, saveResult, disableHover }: IWheelProps & { disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, lifeItems.length, saveResult);

  const leftColumnCount = Math.ceil(lifeItems.length / 2);
  const leftColumnIndexes = Array.from({ length: leftColumnCount }, (_, index) => index);
  const rightColumnIndexes = Array.from(
    { length: lifeItems.length - leftColumnCount },
    (_, index) => leftColumnCount + index,
  );

  const chart = (
    <PieChart className="recharts__pie-chart cursor-pointer" margin={{ top: 28, right: 28, bottom: 28, left: 28 }}>
      {!disableHover && <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />}
      <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
      <Rings rr={rr} sectorCount={lifeItems.length} />
      <InteractionRings
        sectors={sectors}
        onEnter={
          disableHover
            ? () => undefined
            : (idx, lvl) => {
                setHoveredSector(idx);
                setHoveredLevel(lvl);
              }
        }
        onLeave={
          disableHover
            ? () => undefined
            : () => {
                setHoveredSector(null);
                setHoveredLevel(null);
              }
        }
        onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
      />
      <MobileSectorBadges sectors={sectors} lifeItems={lifeItems} />
    </PieChart>
  );

  return (
    <div
      className="w-full h-full min-h-0 min-w-0 cursor-pointer"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.35fr) minmax(0, 1fr)',
        alignItems: 'center',
        gap: 'clamp(8px, 1.2vw, 16px)',
        boxSizing: 'border-box',
        padding: '4px 8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(4px, 0.8vh, 10px)',
          justifyContent: 'center',
          minWidth: 0,
        }}
      >
        {leftColumnIndexes.map((i) => (
          <div key={`side-left-${i}`} style={{ minWidth: 0 }}>
            <MobileTitle idx={i} lifeItems={lifeItems} side="left" useShortTitle={false} />
          </div>
        ))}
      </div>

      <div
        style={{
          width: '100%',
          maxWidth: '100%',
          aspectRatio: '1',
          maxHeight: '100%',
          margin: '0 auto',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          {chart}
        </ResponsiveContainer>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(4px, 0.8vh, 10px)',
          justifyContent: 'center',
          minWidth: 0,
        }}
      >
        {rightColumnIndexes.map((i) => (
          <div key={`side-right-${i}`} style={{ minWidth: 0 }}>
            <MobileTitle idx={i} lifeItems={lifeItems} side="right" useShortTitle={false} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =========================
// Mobile — Portrait
// =========================
function MobilePortraitWheel({
  lifeItems,
  values,
  saveResult,
  fullscreen,
  disableHover,
}: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, lifeItems.length, saveResult);

  const leftColumnCount = Math.ceil(lifeItems.length / 2);
  const leftColumnIndexes = Array.from({ length: leftColumnCount }, (_, index) => index);
  const rightColumnIndexes = Array.from(
    { length: lifeItems.length - leftColumnCount },
    (_, index) => leftColumnCount + index,
  );

  return (
    <div className={`w-full ${fullscreen ? 'h-full' : 'h-auto'} justify-items-center cursor-pointer`}>
      {fullscreen ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            padding: '8px',
          }}
        >
          <div
            style={{
              flex: '0 1 auto',
              maxHeight: '28%',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '12px',
              alignItems: 'start',
              justifyItems: 'stretch',
              padding: '4px 8px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {leftColumnIndexes.map((i) => (
                <div key={`fs-mp-left-${i}`} style={{ fontSize: '14px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} side="left" useShortTitle={false} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {rightColumnIndexes.map((i) => (
                <div key={`fs-mp-right-${i}`} style={{ fontSize: '14px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} side="right" useShortTitle={false} />
                </div>
              ))}
            </div>
          </div>
          <div
            style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ width: 'min(92%, 80vh)', height: '92%', maxHeight: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart
                  className="recharts__pie-chart cursor-pointer"
                  margin={{ top: 28, right: 28, bottom: 28, left: 28 }}
                >
                  {!disableHover && (
                    <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />
                  )}
                  <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                  <Rings rr={rr} sectorCount={lifeItems.length} />
                  <InteractionRings
                    sectors={sectors}
                    onEnter={
                      disableHover
                        ? () => {}
                        : (idx, lvl) => {
                            setHoveredSector(idx);
                            setHoveredLevel(lvl);
                          }
                    }
                    onLeave={
                      disableHover
                        ? () => {}
                        : () => {
                            setHoveredSector(null);
                            setHoveredLevel(null);
                          }
                    }
                    onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                  />
                  <MobileSectorBadges sectors={sectors} lifeItems={lifeItems} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            padding: '0 12px 8px',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              columnGap: '12px',
              alignItems: 'start',
              marginBottom: '8px',
              width: '100%',
              minWidth: 0,
              flex: '0 0 auto',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
              {leftColumnIndexes.map((i) => (
                <div key={`lt-${i}`} style={{ fontSize: '14px', lineHeight: 1.15, minWidth: 0 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} side="left" />
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                textAlign: 'left',
                minWidth: 0,
              }}
            >
              {rightColumnIndexes.map((i) => (
                <div key={`rt-${i}`} style={{ fontSize: '14px', lineHeight: 1.15, minWidth: 0 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} side="right" />
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              flex: '0 0 auto',
              width: 'min(100%, 72vw)',
              aspectRatio: '1',
              maxHeight: 'min(72vw, 360px)',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart
                className="recharts__pie-chart cursor-pointer"
                margin={{ top: 28, right: 28, bottom: 28, left: 28 }}
              >
                {!disableHover && (
                  <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />
                )}
                <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                <Rings rr={rr} sectorCount={lifeItems.length} />
                <InteractionRings
                  sectors={sectors}
                  onEnter={
                    disableHover
                      ? () => {}
                      : (idx, lvl) => {
                          setHoveredSector(idx);
                          setHoveredLevel(lvl);
                        }
                  }
                  onLeave={
                    disableHover
                      ? () => {}
                      : () => {
                          setHoveredSector(null);
                          setHoveredLevel(null);
                        }
                  }
                  onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                />
                <MobileSectorBadges sectors={sectors} lifeItems={lifeItems} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================
// Mobile — Landscape
// =========================
function MobileLandscapeWheel({
  lifeItems,
  values,
  saveResult,
  fullscreen,
  disableHover,
}: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, lifeItems.length, saveResult);

  return (
    <div className="w-full h-full justify-items-center cursor-pointer">
      {fullscreen ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', height: '95%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              columnGap: '12px',
              alignItems: 'center',
              justifyItems: 'start',
              padding: '8px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[7, 6, 5, 4, 0, 1, 2, 3].map((i) => (
                <div key={`fs-ml-left-${i}`} style={{ fontSize: 'clamp(12px, 2.2vw, 16px)', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} side="left" useShortTitle={false} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart
                  className="recharts__pie-chart cursor-pointer"
                  margin={{ top: 28, right: 28, bottom: 28, left: 28 }}
                >
                  {!disableHover && (
                    <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />
                  )}
                  <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                  <Rings rr={rr} sectorCount={lifeItems.length} />
                  <InteractionRings
                    sectors={sectors}
                    onEnter={
                      disableHover
                        ? () => {}
                        : (idx, lvl) => {
                            setHoveredSector(idx);
                            setHoveredLevel(lvl);
                          }
                    }
                    onLeave={
                      disableHover
                        ? () => {}
                        : () => {
                            setHoveredSector(null);
                            setHoveredLevel(null);
                          }
                    }
                    onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                  />
                  <MobileSectorBadges sectors={sectors} lifeItems={lifeItems} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            justifyContent: 'right',
            gap: 'clamp(6px, 2vw, 16px)',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(1px, 0.6vw, 10px)',
              fontSize: 'clamp(12px, 2.2vw, 16px)',
              lineHeight: 1.15,
              maxWidth: '17vw',
              justifyContent: 'center',
            }}
          >
            {[7, 6, 5, 4].map((i) => (
              <div key={`land-l-${i}`}>
                <MobileTitle idx={i} lifeItems={lifeItems} side="left" />
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(1px, 0.6vw, 10px)',
              fontSize: 'clamp(12px, 2.2vw, 16px)',
              lineHeight: 1.15,
              maxWidth: '17vw',
              justifyContent: 'center',
            }}
          >
            {[0, 1, 2, 3].map((i) => (
              <div key={`land-l-${i}`}>
                <MobileTitle idx={i} lifeItems={lifeItems} side="left" />
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'clamp(260px, 60vh, 80vw)',
              height: 'clamp(160px, 55vh, 82vw)',
              maxWidth: '30vw',
              maxHeight: '50vh',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart
                className="recharts__pie-chart cursor-pointer"
                margin={{ top: 28, right: 28, bottom: 28, left: 28 }}
              >
                {!disableHover && (
                  <HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />
                )}
                <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                <Rings rr={rr} sectorCount={lifeItems.length} />
                <InteractionRings
                  sectors={sectors}
                  onEnter={
                    disableHover
                      ? () => {}
                      : (idx, lvl) => {
                          setHoveredSector(idx);
                          setHoveredLevel(lvl);
                        }
                  }
                  onLeave={
                    disableHover
                      ? () => {}
                      : () => {
                          setHoveredSector(null);
                          setHoveredLevel(null);
                        }
                  }
                  onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                />
                <MobileSectorBadges sectors={sectors} lifeItems={lifeItems} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
